import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminCategories() {
    const [cats, setCats] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/categories").then(({ data }) => setCats(data));
    useEffect(() => { document.title = "Admin Categories | Loomara"; load(); }, []);

    const save = async () => {
        try {
            const payload = { name: editing.name, slug: editing.slug, image: editing.image, description: editing.description, order: Number(editing.order) || 0 };
            if (editing.id) await api.put(`/categories/${editing.id}`, payload);
            else await api.post("/categories", payload);
            toast("Saved");
            setEditing(null);
            load();
        } catch (e) { toast(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete "${c.name}"?`)) return;
        await api.delete(`/categories/${c.id}`);
        toast("Deleted"); load();
    };

    return (
        <div data-testid="admin-categories-page">
            <div className="flex items-center justify-between">
                <div><p className="eyebrow">Catalogue</p><h1 className="font-serif text-4xl text-ink-900 mt-2">Categories</h1></div>
                <button onClick={() => setEditing({ name: "", slug: "", image: "", description: "", order: cats.length + 1 })} className="btn-primary" data-testid="admin-new-category-btn"><Plus size={14} /> New Category</button>
            </div>

            <div className="card-soft mt-8 divide-y divide-sage-300/20">
                {cats.map((c) => (
                    <div key={c.id} className="flex items-center gap-4 p-5" data-testid={`admin-cat-${c.slug}`}>
                        <div className="h-14 w-14 rounded-xl bg-ivory-100 overflow-hidden">
                            <img src={c.image} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1">
                            <p className="text-ink-900">{c.name}</p>
                            <p className="text-xs text-ink-500">/{c.slug}</p>
                        </div>
                        <button onClick={() => setEditing(c)} className="text-sage-700" data-testid={`admin-edit-cat-${c.slug}`}><Edit3 size={16} /></button>
                        <button onClick={() => remove(c)} className="text-blush-400" data-testid={`admin-delete-cat-${c.slug}`}><Trash2 size={16} /></button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-[60] bg-ink-900/40 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-lg p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-serif text-2xl">{editing.id ? "Edit Category" : "New Category"}</h2>
                            <button onClick={() => setEditing(null)}><X /></button>
                        </div>
                        <div className="space-y-3">
                            {["name", "slug", "image", "description"].map((f) => (
                                <div key={f}>
                                    <label className="block text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-1.5">{f}</label>
                                    <input value={editing[f] || ""} onChange={(e) => setEditing({ ...editing, [f]: e.target.value })} className="w-full bg-ivory-100 rounded-xl px-4 py-2.5 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid={`cat-${f}`} />
                                </div>
                            ))}
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-1.5">Order</label>
                                <input type="number" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} className="w-full bg-ivory-100 rounded-xl px-4 py-2.5 text-sm border border-sage-300/30" data-testid="cat-order" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                            <button className="btn-primary" onClick={save} data-testid="cat-save">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
